import {useEffect, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {AccionCommonFields} from '@/components/accion-common-fields'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {type Person} from '@/components/person-picker'
import {
  useCreateAction,
  useUpdateAction,
  type SgsstAction,
  type SgsstActionStatus,
} from '@/hooks/sgsst/plan/use-actions'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import type {Evidence} from '@/hooks/sgsst/evidencias/use-evidence'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'

const STATUSES: SgsstActionStatus[] = ['pendiente', 'en_curso', 'cumplida']

/** De dónde nace la acción (fila de la matriz, norma o acción no eficaz), pintado dentro del diálogo. */
export type OrigenMejora = {
  etiqueta: string
  titulo: string
  detalle?: string
  testId: string
}

/** Los cuatro campos que aceptan los endpoints de origen (`MejoraConfigurable` en el backend). */
export type MejoraEditada = {
  descripcion: string
  responsible_employee_id: string
  resources: string | null
  target_date: string
}

export function ActionFormDialog({
  businessId,
  open,
  onOpenChange,
  standardCode,
  standardName,
  action,
  roster,
  evidence,
  initialDescription,
  descripcionPlaceholder,
  initialResponsibleId,
  initialTargetDate,
  origen,
  onCrear,
}: {
  businessId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  standardCode: string | null
  standardName?: string
  action?: SgsstAction | null
  roster: Employee[]
  evidence: Evidence[]
  // Prefill from a suggestion's título; ignored when editing an existing action.
  initialDescription?: string
  /** La sugerencia en gris cuando como valor confundiría: hoy, la medida de una causa, que la
   * tarjeta de contexto ya dice. */
  descripcionPlaceholder?: string
  /** D4: lo que el origen ya derivaba, preelegido para que confirmar siga siendo un clic. */
  initialResponsibleId?: string | null
  initialTargetDate?: string
  origen?: OrigenMejora
  /** D4: sin esto, POST plano a `/actions`; con esto, el endpoint del origen, el único que ata
   * la FK y mantiene su idempotencia. */
  onCrear?: (valores: MejoraEditada) => Promise<unknown>
}) {
  const {t} = useTranslation()
  const create = useCreateAction(businessId)
  const update = useUpdateAction(businessId)
  const [description, setDescription] = useState('')
  const [person, setPerson] = useState<Person | null>(null)
  const [resources, setResources] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [estado, setEstado] = useState<SgsstActionStatus>('pendiente')
  const [soporteId, setSoporteId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)

  useEffect(() => {
    if (!open) return
    setDescription(action?.description ?? initialDescription ?? '')
    setResources(action?.resources ?? '')
    setTargetDate(action?.target_date ?? initialTargetDate ?? '')
    setEstado(action?.status ?? 'pendiente')
    setSoporteId(action?.soporte?.id ?? '')
    const responsible = action?.responsible_employee
    const porDefecto = initialResponsibleId ? roster.find((e) => e.id === initialResponsibleId) : undefined
    setPerson(responsible ? {...(roster.find((e) => e.id === responsible.id) ?? responsible)} : (porDefecto ?? null))
    setError(null)
    setCreando(false)
    // `roster ?? []` is a new array every render: depending on it would reset the half-typed form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, initialResponsibleId, initialDescription, initialTargetDate])

  // El roster llega después de abrir: sin esto el responsable derivado nunca quedaría preelegido.
  useEffect(() => {
    if (!open || !initialResponsibleId || action) return
    setPerson((prev) => prev ?? roster.find((e) => e.id === initialResponsibleId) ?? null)
  }, [open, initialResponsibleId, action, roster])

  const soporteOptions = evidence.filter((e) => (standardCode ? e.standard_code === standardCode : true))
  const pending = create.isPending || update.isPending || creando
  // Una acción que nace de una brecha nace pendiente: su estado se mueve después desde el plan.
  const estadoEditable = !origen || !!action

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!person) {
      setError(t('errors.actionResponsibleXor'))
      return
    }
    // Own Spanish validation instead of the browser's native English «Please fill out this field».
    if (!targetDate) {
      setError(t('errors.actionTargetDateRequired'))
      return
    }
    // KAI-153: sin esto el backend contesta un 422 sin traducir.
    if (!description.trim()) {
      setError(t('errors.actionDescriptionRequired'))
      return
    }
    const shared = {
      description,
      responsible_employee_id: person.id,
      resources: resources.trim() ? resources : null,
      target_date: targetDate,
      status: estado,
    }
    const options = {
      onSuccess: () => onOpenChange(false),
      onError: (err: unknown) => setError(translateApiError(err)),
    }
    if (action) {
      const statusChanged = estado !== action.status
      update.mutate(
        {id: action.id, ...shared, ...(estado === 'cumplida' ? {soporte_evidence_id: soporteId || null} : {})},
        {
          ...options,
          onSuccess: () => {
            if (statusChanged)
              track('action_advanced', {
                business_id: businessId,
                standard_code: action.standard_code ?? 'transversal',
                status: estado,
              })
            options.onSuccess()
          },
        }
      )
    } else if (onCrear) {
      setError(null)
      setCreando(true)
      onCrear({
        descripcion: description,
        responsible_employee_id: person.id,
        resources: shared.resources,
        target_date: targetDate,
      })
        .then(() => onOpenChange(false))
        .catch((err: unknown) => setError(translateApiError(err)))
        .finally(() => setCreando(false))
    } else {
      create.mutate({standard_code: standardCode, ...shared}, options)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={origen?.testId}>
        <DialogHeader>
          <DialogTitle>{t(action ? 'plan.form.editTitle' : 'plan.form.createTitle')}</DialogTitle>
        </DialogHeader>
        {(!origen || standardCode) && (
          <p className="font-mono text-xs text-muted-foreground">
            {standardCode ? `${standardCode} · ${standardName ?? ''}` : t('plan.transversal')}
          </p>
        )}
        {origen && (
          <div className="rounded-lg border border-border bg-secondary/40 px-3 py-2.5" data-testid="origen-banner">
            <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
              {origen.etiqueta}
            </p>
            <p className="mt-1 text-sm text-foreground">{origen.titulo}</p>
            {origen.detalle && <p className="mt-0.5 text-xs text-muted-foreground">{origen.detalle}</p>}
          </div>
        )}
        <form onSubmit={submit} className="space-y-4" data-testid="action-form">
          <AccionCommonFields
            idPrefix="action"
            businessId={businessId}
            roster={roster}
            description={description}
            onDescriptionChange={setDescription}
            descripcionPlaceholder={descripcionPlaceholder}
            person={person}
            onPersonChange={setPerson}
            resources={resources}
            onResourcesChange={setResources}
          />
          <div className={estadoEditable ? 'grid grid-cols-2 gap-4' : 'space-y-2'}>
            <div className="space-y-2">
              <Label htmlFor="action_target_date">
                {t('plan.form.targetDate')} <span className="text-muted-foreground">{t('plan.form.requiredMark')}</span>
              </Label>
              <Input
                id="action_target_date"
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="font-mono"
                data-testid="action-target-date"
              />
            </div>
            {estadoEditable && (
              <div className="space-y-2">
                <Label htmlFor="action_status">{t('plan.form.status')}</Label>
                <select
                  id="action_status"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value as SgsstActionStatus)}
                  className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                  {STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {t(`plan.status.${status}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {action && estado === 'cumplida' && (
            <div className="space-y-2">
              <Label htmlFor="action_soporte">{t('plan.form.soporte')}</Label>
              <EntitySearchSelect
                id="action_soporte"
                items={soporteOptions}
                value={soporteOptions.find((item) => item.id === soporteId) ?? null}
                getKey={(item) => item.id}
                filterText={(item) => item.filename}
                renderOption={(item) => item.filename}
                renderTriggerValue={(item) => item.filename}
                placeholder={t('plan.form.noSoporte')}
                searchPlaceholder={t('plan.form.soporteSearchPlaceholder')}
                noResultsLabel={t('plan.form.soporteNoResults')}
                clearLabel={t('plan.form.noSoporte')}
                testId="action_soporte"
                onPick={(item) => setSoporteId(item?.id ?? '')}
              />
            </div>
          )}
          <FormDialogFooter
            error={error}
            pending={pending}
            submitLabel={t(action ? 'plan.form.submitEdit' : 'plan.form.submitCreate')}
            onCancel={() => onOpenChange(false)}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
